import React, { useCallback, useRef } from 'react'
import {
    compName_character,
    livePortsNumber,
} from '../logic/panels/helpers/LiveButton'
import { useSelector } from 'react-redux'
import RGBIndividualSlider from './RGBIndividualSlider'
import InputNumber from './InputNumber'
import './RGBSlider.css'

function RGBSlider({
    sliders = [],
    outputStates = {},
    onChange,
    generateSliderProps,
    label,
    min = 0,
    max = 100,
    port, // hardware port key, e.g. 'servo_motor1'
    isLiveStep,
}) {
    const worker = useSelector((state) => state.worker)
    const webSerial = useSelector((state) => state.webSerial)
    const isLiveActive = !!webSerial?.isConnected
    const debounceTimer = useRef(null)

    // Send Live Write (LW) — Toki RGB format: [L, W, L, K, 0, R, G, B]
    const sendRGBLiveWrite = useCallback(
        (r, g, b) => {
            if (!worker || !webSerial?.isConnected) return
            worker.postMessage({
                type: 'writeArray',
                value: [76, 87, 76, 75, 0, r, g, b],
            })
        },
        [worker, webSerial]
    )

    // Called for every slider/input change — also sends live bytes when active
    const handleSliderChange = useCallback(
        (mapping, value) => {
            onChange(mapping, value)
            if (isLiveStep) return
            if (!isLiveActive || !webSerial?.isConnected) return

            // Compute updated RGB inline so we don't read stale outputStates
            let r = 0,
                g = 0,
                b = 0
            sliders.forEach((s) => {
                const v =
                    s.mapping === mapping
                        ? value
                        : Number(outputStates[s.mapping]) || 0
                const n = s.name.toUpperCase()
                if (n.startsWith('R')) r = v
                else if (n.startsWith('G')) g = v
                else if (n.startsWith('B')) b = v
            })

            if (debounceTimer.current) clearTimeout(debounceTimer.current)
            debounceTimer.current = setTimeout(
                () => sendRGBLiveWrite(r, g, b),
                50
            )
        },
        [
            onChange,
            isLiveStep,
            isLiveActive,
            webSerial,
            sliders,
            outputStates,
            sendRGBLiveWrite,
        ]
    )

    return (
        <div className="play-rgb-slider">
            {/* Header matches the play-motor-slider layout + Live Button */}
            {label && (
                <div className="play-rgb-slider__header">
                    <span className="play-rgb-slider__name">{label}</span>
                </div>
            )}

            <div className="play-rgb-slider__tracks">
                {sliders.map((slider, idx) => {
                    const val = outputStates[slider.mapping] || 0
                    const currentImg =
                        val > 0 ? slider.activeImg : slider.inactiveImg
                    const compName = slider.legacyName || slider.mapping
                    const dynamicProps = generateSliderProps(
                        slider.mapping,
                        compName
                    )

                    return (
                        <div key={idx} className="play-rgb-slider__channel">
                            {/* Slider row */}
                            <div className="play-rgb-slider__row">
                                <span
                                    className="play-rgb-slider__color-label"
                                    style={{
                                        color: slider.name.toLowerCase(),
                                        width: '20px',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {slider.name.charAt(0)}
                                </span>

                                <div
                                    className="play-rgb-slider__track-cell"
                                    style={{ flexGrow: 1 }}
                                >
                                    <RGBIndividualSlider
                                        rangImgName={currentImg}
                                        title=""
                                        componentName={compName}
                                        onChangehandler={(v) =>
                                            handleSliderChange(
                                                slider.mapping,
                                                v
                                            )
                                        }
                                        leftEyeData={() => {}}
                                        disable={false}
                                        webSerial={{ isConnected: true }}
                                        {...dynamicProps}
                                    />
                                </div>
                            </div>

                            {/* Input + Min/Max labels */}
                            <div
                                className="play-rgb-slider__input-row"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginTop: '10px',
                                }}
                            >
                                <div style={{ width: '60px' }}>
                                    <InputNumber
                                        value={val}
                                        onChange={(v) =>
                                            handleSliderChange(
                                                slider.mapping,
                                                v
                                            )
                                        }
                                        min={min}
                                        max={max}
                                        disabled={false}
                                    />
                                </div>
                                <div
                                    className="play-rgb-slider__minmax"
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        flexGrow: 1,
                                        marginLeft: '10px',
                                        fontSize: '0.8rem',
                                        color: '#888',
                                    }}
                                >
                                    <p className="play-rgb-slider__minmax-left">
                                        {min}
                                    </p>
                                    <p className="play-rgb-slider__minmax-right">
                                        {max}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export default RGBSlider
